import {
  useState, useCallback, useMemo,
} from 'react';
import { useDispatch, useStore } from 'react-redux';
import { fetchLastPlannedBlock } from '../ducks/plannerActions';
import {
  getCurrentWeek,
  dateToWeek,
  addWeeks,
} from '../utils/weekUtils';
import { generatePlannerPDF } from '../services/pdfExportService';
import { showErrorToast } from '../../../shared/utils/toastManager';

/**
 * Hook to manage PDF export functionality.
 * Handles export modal state, PDF generation, and last planned block tracking.
 * Fetches the last planned block on-demand when export is requested to ensure fresh data.
 *
 * @param {Object} options
 * @param {string|number} options.planId - The plan ID
 * @param {string} options.selectedCalendarName - The selected calendar name
 * @param {string} options.accountCode - The account code
 * @param {string} options.orgLogo - The organization logo URL
 * @param {string|null} options.calendarNameStorageKey - Account-scoped storage key
 * @returns {Object} - Export state and handlers
 */
const usePlannerExport = ({
  planId,
  selectedCalendarName,
  accountCode,
  orgLogo,
  calendarNameStorageKey = null,
}) => {
  const dispatch = useDispatch();
  const store = useStore();

  const [isExportModalOpen, setIsExportModalOpen] = useState(false);
  const [exportLoadingMessage, setExportLoadingMessage] = useState(null);
  const [lastPlannedBlock, setLastPlannedBlock] = useState(null);
  const [isFetchingBlock, setIsFetchingBlock] = useState(false);

  // Calculate default export date range (memoized to prevent recalculation on every render)
  const { exportDefaultFromWeek, exportDefaultToWeek } = useMemo(() => {
    const currentWeek = getCurrentWeek();
    return {
      exportDefaultFromWeek: currentWeek,
      exportDefaultToWeek: lastPlannedBlock?.endDate
        ? dateToWeek(lastPlannedBlock.endDate)
        : addWeeks(currentWeek, 12),
    };
  }, [lastPlannedBlock?.endDate]);

  // Fetch last planned block when export is requested, then open modal
  const handleExport = useCallback(async () => {
    if (!planId || !accountCode) {
      setIsExportModalOpen(true);
      return;
    }

    setIsFetchingBlock(true);
    try {
      const block = await dispatch(fetchLastPlannedBlock(accountCode, planId));
      setLastPlannedBlock(block);
    } catch {
      setLastPlannedBlock(null);
    } finally {
      setIsFetchingBlock(false);
      setIsExportModalOpen(true);
    }
  }, [planId, accountCode, dispatch]);

  const handleCloseExportModal = useCallback(() => {
    setIsExportModalOpen(false);
  }, []);

  const handleExportToPDF = useCallback(async (fromWeek, toWeek) => {
    setIsExportModalOpen(false);

    // Use calendar name from Redux, fallback to localStorage, then to default
    let exportCalendarName = selectedCalendarName;
    if (!exportCalendarName && calendarNameStorageKey) {
      exportCalendarName = localStorage.getItem(calendarNameStorageKey);
    }
    if (!exportCalendarName) {
      exportCalendarName = localStorage.getItem('teambuildr_planner_last_calendar_name');
    }
    if (!exportCalendarName) {
      exportCalendarName = 'Training Plan';
    }

    try {
      // Call PDF service directly - no need to switch modes
      // The service fetches data from API and renders in off-screen containers
      await generatePlannerPDF({
        fromWeek,
        toWeek,
        calendarName: exportCalendarName,
        orgLogo,
        getState: () => store.getState(),
        onProgress: setExportLoadingMessage,
      });
    } catch (error) {
      showErrorToast(`Failed to export PDF: ${error.message || 'Unknown error'}`);
    } finally {
      setExportLoadingMessage(null);
    }
  }, [selectedCalendarName, orgLogo, calendarNameStorageKey, store]);

  return {
    isExportModalOpen,
    exportLoadingMessage,
    exportDefaultFromWeek,
    exportDefaultToWeek,
    isFetchingBlock,
    handleExport,
    handleCloseExportModal,
    handleExportToPDF,
  };
};

export default usePlannerExport;
